/* Dynamic Island: a pílula preta no topo do conteúdo. Em repouso mostra o ambiente;
   validação, envio à Sefin e confirmações passam por ela, no lugar de toasts. Nunca pede
   decisão (decisão é modal). O estado fica fora do React para sobreviver à troca de tela:
   "NFS-e nº 3 autorizada" aparece já na página da nota. */
import React, { useEffect, useSyncExternalStore } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle, Info, Warning, XCircle } from 'phosphor-react';
import { useNfseEmissao } from '../../../../../hooks/useNfseEmissao';
import { NfseAmbiente } from '../../../../../types/nfseEmissao';
import { C, MONO, SPRING } from './ui';

type IslandTone = 'ok' | 'bad' | 'info' | 'run' | 'warn';

type IslandState =
  | { mode: 'idle' }
  | { mode: 'toast'; text: string; tone: IslandTone; meta?: string; id: number }
  | { mode: 'live'; title: string; subtitle?: string }
  | { mode: 'info' };

let estado: IslandState = { mode: 'idle' };
let timer: ReturnType<typeof setTimeout> | undefined;
const ouvintes = new Set<() => void>();

const definir = (novo: IslandState, ms?: number) => {
  clearTimeout(timer);
  estado = novo;
  ouvintes.forEach(fn => fn());
  if (ms) timer = setTimeout(() => definir({ mode: 'idle' }), ms);
};

export const island = {
  /** Mensagem curta; some sozinha (sticky mantém até a próxima). */
  notify: (
    text: string,
    opts: { tone?: IslandTone; meta?: string; sticky?: boolean; ms?: number } = {},
  ) =>
    definir(
      { mode: 'toast', text, tone: opts.tone ?? 'ok', meta: opts.meta, id: Date.now() },
      opts.sticky ? undefined : (opts.ms ?? 3200),
    ),
  /** Operação em andamento (envio à Sefin): fica até o resultado. */
  live: (title: string, subtitle?: string) => definir({ mode: 'live', title, subtitle }),
  idle: () => definir({ mode: 'idle' }),
};

const assinar = (fn: () => void) => {
  ouvintes.add(fn);
  return () => ouvintes.delete(fn);
};

const TOM: Record<IslandTone, { cor: string; icon: React.ReactNode }> = {
  ok: { cor: '#4ADE80', icon: <CheckCircle size={18} weight="fill" /> },
  bad: { cor: '#F87171', icon: <XCircle size={18} weight="fill" /> },
  warn: { cor: '#FBBF24', icon: <Warning size={18} weight="fill" /> },
  info: { cor: C.islandAccent, icon: <Info size={18} weight="fill" /> },
  run: { cor: C.islandAccent, icon: null },
};

function Girando() {
  return (
    <motion.svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      animate={{ rotate: 360 }}
      transition={{ duration: 0.9, repeat: Infinity, ease: 'linear' }}
      aria-hidden
    >
      <circle cx="9" cy="9" r="7" fill="none" stroke="rgba(255,255,255,.18)" strokeWidth="2.4" />
      <path
        d="M9 2a7 7 0 0 1 7 7"
        fill="none"
        stroke={C.islandAccent}
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </motion.svg>
  );
}

function Linha({ tom, label, meta }: { tom: IslandTone; label: string; meta?: string }) {
  return (
    <Flex align="center" gap={2.5} py={1}>
      <Box w="7px" h="7px" borderRadius="full" bg={TOM[tom].cor} flex="none" />
      <Text flex={1} fontSize="12.5px" color={C.onIsland}>
        {label}
      </Text>
      {meta && (
        <Text fontFamily={MONO} fontSize="11px" color={C.onIslandMuted}>
          {meta}
        </Text>
      )}
    </Flex>
  );
}

export function NfseIsland({ ambiente }: { ambiente?: NfseAmbiente }) {
  const s = useSyncExternalStore(assinar, () => estado);
  const { getPainel } = useNfseEmissao();
  // Mesmo cache da visão geral: o estado geral da ilha não custa outra chamada.
  const painel = useQuery({ queryKey: ['nfse-painel', 'mes'], queryFn: getPainel }).data;
  const amb = ambiente ?? painel?.ambiente;
  const prod = amb === 'producao';
  let rotuloAmbiente = 'NFS-e';
  if (amb) rotuloAmbiente = prod ? 'PRODUÇÃO' : 'HOMOLOGAÇÃO';

  useEffect(() => () => clearTimeout(timer), []);

  const tocar = () => {
    if (s.mode === 'idle') definir({ mode: 'info' }, 6000);
    else if (s.mode !== 'live') definir({ mode: 'idle' });
  };

  let conteudo: React.ReactNode;
  if (s.mode === 'toast') {
    conteudo = (
      <Flex align="center" gap={2.5} px={4} h="36px">
        <Box color={TOM[s.tone].cor}>{s.tone === 'run' ? <Girando /> : TOM[s.tone].icon}</Box>
        <Text fontSize="13px" fontWeight={600} color={C.onIsland} whiteSpace="nowrap">
          {s.text}
        </Text>
        {s.meta && (
          <Text fontFamily={MONO} fontSize="11px" color={C.onIslandMuted} whiteSpace="nowrap">
            {s.meta}
          </Text>
        )}
      </Flex>
    );
  } else if (s.mode === 'live') {
    conteudo = (
      <Flex align="center" gap={3} px={4} py={3} minW="300px">
        <Girando />
        <Box>
          <Text fontSize="13px" fontWeight={600} color={C.onIsland}>
            {s.title}
          </Text>
          {s.subtitle && (
            <Text fontFamily={MONO} fontSize="11px" color={C.onIslandMuted}>
              {s.subtitle}
            </Text>
          )}
        </Box>
      </Flex>
    );
  } else if (s.mode === 'info') {
    const pend = (painel?.rejeitadas ?? 0) + (painel?.rascunhos ?? 0);
    const certs = (painel?.certificados.vencendo ?? 0) + (painel?.certificados.vencidos ?? 0);
    conteudo = (
      <Box px={4} py={3} minW="320px">
        <Linha
          tom={prod ? 'ok' : 'warn'}
          label={prod ? 'Produção · notas com valor jurídico' : 'Homologação · sem valor jurídico'}
          meta={`tpAmb ${prod ? 1 : 2}`}
        />
        <Linha
          tom={pend ? 'warn' : 'ok'}
          label={`${painel?.rejeitadas ?? 0} rejeição(ões) e ${painel?.rascunhos ?? 0} rascunho(s) pendentes`}
          meta="notas"
        />
        <Linha
          tom={certs ? 'warn' : 'ok'}
          label={`${certs} certificado(s) pedem atenção`}
          meta="30 dias"
        />
      </Box>
    );
  } else {
    conteudo = (
      <Flex align="center" gap={2} px={4} h="36px">
        <motion.span
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: prod ? '#4ADE80' : '#FBBF24',
          }}
          animate={prod ? undefined : { opacity: [1, 0.3, 1] }}
          transition={{ duration: 1.6, repeat: Infinity }}
        />
        <Text
          fontFamily={MONO}
          fontSize="11px"
          fontWeight={600}
          letterSpacing="0.8px"
          color={C.onIsland}
        >
          {rotuloAmbiente}
        </Text>
      </Flex>
    );
  }

  return (
    <Flex position="sticky" top={3} zIndex={20} justify="center" pointerEvents="none" mb={-2}>
      <motion.button
        type="button"
        layout
        transition={SPRING}
        onClick={tocar}
        aria-live="polite"
        aria-label="Estado da emissão de NFS-e"
        style={{
          pointerEvents: 'auto',
          background: C.island,
          borderRadius: s.mode === 'info' || s.mode === 'live' ? 28 : 999,
          boxShadow: '0 10px 30px rgba(0,0,0,.28), 0 2px 6px rgba(0,0,0,.2)',
          overflow: 'hidden',
          cursor: 'pointer',
          border: 0,
        }}
      >
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.div
            key={s.mode === 'toast' ? `t${s.id}` : s.mode}
            initial={{ opacity: 0, filter: 'blur(4px)' }}
            animate={{ opacity: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, filter: 'blur(4px)' }}
            transition={{ duration: 0.2 }}
          >
            {conteudo}
          </motion.div>
        </AnimatePresence>
      </motion.button>
    </Flex>
  );
}
